<template>
  <!-- text-xs on the shell: every child sets it too, so anything the SHELL
       itself renders (the Input banner) would otherwise be the only markup here
       at the page default size. -->
  <div class="text-xs">
    <!-- Patient detail REPLACES the tabs rather than nesting inside one: you
         arrive here from search OR from a contract roster, so it is not a child
         of either. The tab stays in the path (/data/mdr/<tab>/patients/<pm>),
         so Back returns where you came, even from a pasted link. -->
    <MDR_Patient
      v-if="openPatient"
      :patient-master="openPatient"
      @back="closePatient"
    />

    <template v-else>
      <Page_Tabs
        :tabs="tabs"
        :initial-tab="activeTab"
        class="pb-3"
        no-border
        @select="selectTab"
      />

      <!-- Kept mounted behind v-show: returning to the contract grid is then
           instant and needs no refetch (it is the slowest of the three). -->
      <div v-if="visited.has('Search')" v-show="activeTab === 'Search'">
        <MDR_Search @open="openFromSearch" />
      </div>
      <div v-if="visited.has('Contracts')" v-show="activeTab === 'Contracts'">
        <MDR_Contracts @open-patient="openFromRoster" />
      </div>
      <div v-if="visited.has('Feeds')" v-show="activeTab === 'Feeds'">
        <MDR_Feeds />
      </div>
      <div v-if="visited.has('Input')" v-show="activeTab === 'Input'">
        <!-- THE ONLY WRITE SURFACE IN THIS MODULE. Everything else reads the
             published run; this edits the live PATIENT_ALL view that FEEDS the
             next run, so a bad save breaks the Saturday build rather than this
             screen. Superuser-only and deliberately last in the tab order. -->
        <div
          class="mb-3 px-3 py-2 border border-amber-200 bg-amber-50 rounded
                 text-amber-800"
        >
          Editing the live feeder view. A save redeploys
          <span class="font-mono">EMPI_INPUT.PATIENT_ALL</span> immediately and
          changes what the next weekly run ingests — it does not affect the
          published data on the other tabs.
        </div>
        <MDR_Input />
      </div>
    </template>
  </div>
</template>

<script>
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import MDR_Search from '@/components/ETL_Utility/mdr/MDR_Search.vue';
import MDR_Contracts from '@/components/ETL_Utility/mdr/MDR_Contracts.vue';
import MDR_Feeds from '@/components/ETL_Utility/mdr/MDR_Feeds.vue';
import MDR_Patient from '@/components/ETL_Utility/mdr/MDR_Patient.vue';
import MDR_Input from '@/components/ETL_Utility/mdr/MDR_Input.vue';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import { dataMdrLocation, mdrTabLabel } from '../../../../router/data_routes';

const MDR_ROUTES = ['DataMdrTab', 'DataMdrPatient'];

export default {
  name: 'MDR',
  components: {
    Page_Tabs,
    MDR_Search,
    MDR_Contracts,
    MDR_Feeds,
    MDR_Patient,
    MDR_Input,
  },
  data() {
    return {
      activeTab: null,
      visited: new Set(),
      // Mirrors of the URL, synced by the $route watcher. They are only
      // written while an MDR route is shown: the pane stays mounted behind
      // v-show while other /data tabs are open, and must not reset then.
      openPatient: null,
    };
  },
  computed: {
    // Search and the patient record are PHI. Hide the tab rather than letting
    // the user click into a 403 — the backend enforces it either way
    // (views._EMPIPHIView); this only avoids a dead end.
    //
    // MIRRORS THE BACKEND RULE, which is not simply "require data_phi":
    // tenants model PHI differently. IHCI grants module_data_mdr to four roles
    // and has no data_phi row at all, so requiring it would hide search from
    // every MDR user they have. If the tenant publishes data_phi in its feature
    // set, the user must hold it; if the tenant does not use data_phi, MDR
    // access is the decision. Read from tenantAccess.features, which is the
    // tenant's own enabled set.
    isSuperuser() {
      return !!this.$store.getters['auth/current_user']?.is_superuser;
    },
    tenantUsesPhi() {
      const slug = this.$store.getters['tenant/current_tenant_slug'];
      const features = this.$store.state.tenant.tenantAccess?.[slug]?.features;
      // A user whose own grants include it proves the tenant publishes it.
      return Array.isArray(features) && features.includes(FEATURES.DATA_PHI);
    },
    canSeePatients() {
      if (this.isSuperuser) return true;
      // canAccessFeature reads the same precomputed set; when the tenant does
      // not model PHI at all this is false for everyone, so fall back to the
      // MDR grant that got them onto this tab.
      return this.tenantUsesPhi
        ? canAccessFeature(FEATURES.DATA_PHI)
        : canAccessFeature(FEATURES.MODULE_DATA_MDR);
    },
    // Everything syncFromRoute reads, so the watcher fires on every navigation.
    routeKey() {
      const r = this.$route;
      return r ? [r.name, r.params?.tab, r.params?.mdrTab, r.params?.patient].join('|') : '';
    },
    tabs() {
      const t = [];
      if (this.canSeePatients) {
        t.push({ label: 'Search', icon: ['fas', 'magnifying-glass'] });
      }
      t.push({ label: 'Contracts', icon: ['fas', 'file-contract'] });
      t.push({ label: 'Feeds', icon: ['fas', 'database'] });
      // Input edits the feeder view, not tenant data, so it is gated on
      // is_superuser rather than a feature flag — same rule it had as its own
      // top-level tab. Last in the order because it is the only writer.
      if (this.isSuperuser) {
        t.push({ label: 'Input', icon: ['fas', 'pen-to-square'] });
      }
      return t;
    },
  },
  watch: {
    routeKey: {
      immediate: true,
      handler() {
        this.syncFromRoute();
      },
    },
    activeTab: {
      immediate: true,
      handler(t) {
        if (t) this.visited.add(t);
      },
    },
  },
  methods: {
    defaultTab() {
      return this.canSeePatients ? 'Search' : 'Contracts';
    },
    syncFromRoute() {
      const route = this.$route;
      if (!route) return;
      const isBare = route.name === 'DataTab' && route.params?.tab === 'mdr';
      if (!isBare && !MDR_ROUTES.includes(route.name)) return;

      const label = isBare ? null : mdrTabLabel(route.params.mdrTab);
      // Bare /data/mdr, or a tab this user can't open (a shared Search link
      // without PHI access, Input without superuser): canonicalise to the
      // default tab. Drops any patient in the path along with it.
      if (!label || !this.tabs.some((t) => t.label === label)) {
        const tab = this.defaultTab();
        this.activeTab = tab;
        this.openPatient = null;
        this.$router.replace(dataMdrLocation({ tab }));
        return;
      }
      this.activeTab = label;
      this.openPatient = route.name === 'DataMdrPatient' ? route.params.patient || null : null;
    },
    selectTab(tab) {
      if (tab !== this.activeTab || this.openPatient) this.$router.push(dataMdrLocation({ tab }));
    },
    openFromSearch(pm) {
      this.$router.push(dataMdrLocation({ tab: 'Search', patient: pm }));
    },
    openFromRoster(pm) {
      this.$router.push(dataMdrLocation({ tab: 'Contracts', patient: pm }));
    },
    // A pasted link has no history, so push the tab URL rather than back().
    closePatient() {
      this.$router.push(dataMdrLocation({ tab: this.activeTab || this.defaultTab() }));
    },
  },
};
</script>
